import React from 'react';
import {
  FormProvider,
  useForm,
  UseFormProps,
  FieldValues,
  SubmitHandler,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

export interface FormProps<T extends FieldValues> {
  schema: z.ZodType<T>;
  defaultValues?: UseFormProps<T>['defaultValues'];
  onSubmit: SubmitHandler<T>;
  children: React.ReactNode;
  className?: string;
  id?: string;
}

/**
 * Form wrapper that integrates react-hook-form with zod validation.
 * Use with FormField, Input, Select etc. from components/ui.
 *
 * @example
 * const schema = z.object({ username: z.string().min(1, 'Required'), password: z.string().min(1, 'Required') });
 * <Form schema={schema} onSubmit={(data) => console.log(data)}>
 *   <FormField name="username" label="Username" />
 *   <FormField name="password" label="Password" type="password" />
 *   <Button type="submit">Submit</Button>
 * </Form>
 */
export function Form<T extends FieldValues>({
  schema,
  defaultValues,
  onSubmit,
  children,
  className,
  id,
}: FormProps<T>): React.ReactElement {
  const methods = useForm<T>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  return (
    <FormProvider {...methods}>
      <form id={id} className={className} onSubmit={methods.handleSubmit(onSubmit)} noValidate>
        {children}
      </form>
    </FormProvider>
  );
}
